// @avantia common-code
import { SMap } from '@avantia/client-and-server-utilities';
import { HpValidator } from '@avantia/form-validation';
import { HpEither } from '@avantia/logging-and-errors';
import {
  HpActionPropTypes,
  HpAddressLookupItemResponse,
  HpAnswer,
  HpAnswerValueTypes,
  HpCommonQuestion,
  HpControlActionTypes,
  HpCustomData,
  HpHelpInfo,
  HpQuestionLookupCommonItem
} from '@avantia/questionset-model';
import { HpPrerequisiteThisReference } from '@avantia/questionset-script-parser';
import { HpDynamicButtonClickAction } from '../actions/dynamicButtonClickActionTypes';
import { HpAction, HpDispatchFunction } from './actionTypes';
import { HpClientButtonData } from './buttonTypesClient';
import { HpFieldChangedPayload } from './fieldChangedActionTypes';
import {
  HpClientAnswerExpressionFunction,
  HpClientConditionFunction,
  HpClientDynamicFunctionProps,
  HpClientLookupExpressionFunction
} from './functionTypes';
import { HpClientInputTextAndStyle } from './inputTypesClient';
import { HpInfoPositionType } from './widgetUtilitiesTypes';

export const activeSectionId = 'activeSectionId';
export const customDataId = 'customData';
export const highestSectionStartedPositionId = 'highestSectionStartedPosition';
export const metadataId = 'metadata';
export const missingOptionsId = 'missingOptions';
export const showParticularSectionId = 'showParticularSection';

export const specialQuestionIds = Object.freeze([
  activeSectionId,
  customDataId,
  highestSectionStartedPositionId,
  metadataId,
  missingOptionsId,
  showParticularSectionId
]);

export type HpClientQuestionMap = SMap<HpClientQuestion>;
export type HpClientDependentAnswerMap = SMap<HpClientDependentAnswer[]>;
export type HpDependentActionFunction<ResponseModelT> = (
  dispatch: HpDispatchFunction<ResponseModelT>
) => Promise<void | boolean>;

export interface HpClientDependentAnswer {
  answer: HpAnswerValueTypes;
  answerExpr?: HpClientAnswerExpressionFunction;
  condition: HpClientConditionFunction;
}

export interface HpClientDependentAction {
  action: HpControlActionTypes;
  condition?: HpClientConditionFunction;
  props?: HpActionPropTypes;
}

export interface HpClientQuestion extends HpCommonQuestion {
  prerequisites: HpClientConditionFunction[];
  calculatedAnswer?: HpClientAnswerExpressionFunction;
  data: HpClientQuestionData | HpClientButtonData;
  position: number;
  defaultExpr?: string;
}

export interface HpClientQuestionData {
  customData?: HpCustomData;
  dependentActions?: HpClientDependentAction[];
  dependentAnswers?: HpClientDependentAnswerMap;
  helpInfo?: HpHelpInfo;
  lookup: HpClientQuestionLookupItemMap;
  lookupExpr?: HpClientLookupExpressionFunction;
  textAndStyle: HpClientInputTextAndStyle;
  validation: HpValidator<unknown>[];
}

export interface HpClientAnswer extends HpAnswer, HpClientPartialAnswer {
  // empty
}

export interface HpClientPartialAnswer {
  partial?: unknown | undefined;
}

export type HpClientAnswerMap = SMap<HpClientAnswer>;

export interface HpAddressLookupPartialAnswer {
  addresses: HpAddressLookupItemResponse[];
}

export type HpClientQuestionLookupItemMap = SMap<HpClientQuestionLookupItem | null>;

export interface HpClientQuestionLookupItem extends HpQuestionLookupCommonItem {
  condition?: HpClientConditionFunction;
}

export interface HpClientHelpInfoConditionalText {
  condition: HpClientConditionFunction;
  isIconHidden?: boolean | undefined;
  position?: HpInfoPositionType;
  text: string;
}

export interface HpClientQuestionTextConditionalText {
  condition: HpClientConditionFunction;
  subtext?: string;
  text: string;
}

export type HpClientCustomActionResponseModelTypes = HpAction<any> | false | undefined;

export type HpClientCustomActionResponse =
  | HpClientCustomActionResponseModelTypes
  | Promise<HpClientCustomActionResponseModelTypes>;

export interface HpClientCustomActionFunctionProps extends HpClientDynamicFunctionProps {
  dispatch: HpDispatchFunction<unknown>;
  payload?: HpEither<HpFieldChangedPayload, HpDynamicButtonClickAction>;
}

export type HpClientCustomActionFunction = (
  this: HpPrerequisiteThisReference,
  props: HpClientCustomActionFunctionProps
) => HpClientCustomActionResponse;
